import React, { useState } from 'react';
import { FormStepId } from '../types';
import { useEnquiryStore } from '../store/useEnquiryStore';
import { useAuth } from '../contexts/AuthContext';

interface FormNavigationProps {
  currentStep: FormStepId;
  onStepChange: (step: FormStepId) => void;
  isFormValid: boolean;
  showEnquiryList: boolean;
  onToggleEnquiryList: () => void;
  onCreateNewEnquiry?: () => void;
}

const steps: { id: FormStepId; title: string; order: number }[] = [
  { id: 'customer', title: 'Customer Details', order: 1 },
  { id: 'appointment', title: 'Appointment', order: 2 },
  { id: 'quote', title: 'Quote Details', order: 3 },
  { id: 'outcome', title: 'Outcome', order: 4 },
  { id: 'specifications', title: 'Specifications', order: 5 },
  { id: 'attachments', title: 'Attachments', order: 6 },
  { id: 'review', title: 'Review', order: 7 }
];

export const FormNavigation: React.FC<FormNavigationProps> = ({
  currentStep,
  onStepChange,
  isFormValid,
  showEnquiryList,
  onToggleEnquiryList,
  onCreateNewEnquiry
}) => {
  const { isOnline, createNewEnquiry } = useEnquiryStore();
  const { user, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const currentStepOrder = steps.find(s => s.id === currentStep)?.order || 1;

  const goToNextStep = () => {
    const nextStep = steps.find(s => s.order === currentStepOrder + 1);
    if (nextStep) {
      onStepChange(nextStep.id);
    }
  };

  const goToPreviousStep = () => {
    const prevStep = steps.find(s => s.order === currentStepOrder - 1);
    if (prevStep) {
      onStepChange(prevStep.id);
    }
  };

  return (
    <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3 lg:py-4">
          {/* Left side - Title and Status */}
          <div className="flex items-center space-x-2 lg:space-x-8">
            <div className="flex items-center space-x-2 lg:space-x-4">
              <h1 className="text-lg lg:text-2xl font-bold text-gray-900 truncate">
                <span className="hidden sm:inline">Blinds Enquiry Form</span>
                <span className="sm:hidden">Blinds Enquiry</span>
              </h1>
              <div className={`px-2 py-1 text-xs rounded-full ${
                isOnline ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
              }`}>
                <span className="hidden sm:inline">{isOnline ? '🟢 Online' : '🟡 Offline'}</span>
                <span className="sm:hidden">{isOnline ? '🟢' : '🟡'}</span>
              </div>
            </div>
            
            {/* Desktop Steps Navigation */}
            {!showEnquiryList && (
              <div className="hidden lg:flex space-x-1">
                {steps.map((step) => (
                  <button
                    key={step.id}
                    onClick={() => onStepChange(step.id)}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      currentStep === step.id
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {step.order}. {step.title}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Right side - Actions and Menu */}
          <div className="flex items-center space-x-1 lg:space-x-2">
            {/* Desktop User Info */}
            <div className="hidden lg:flex items-center space-x-2 mr-4">
              <div className="text-sm text-gray-600">
                Welcome, <span className="font-medium">{user?.email}</span>
              </div>
              <button
                onClick={signOut}
                className="text-sm text-gray-500 hover:text-gray-700 p-1"
                title="Sign out"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              title="Menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            {/* Desktop Navigation Buttons */}
            <div className="hidden lg:flex items-center space-x-2">
              <button
                onClick={onToggleEnquiryList}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {showEnquiryList ? 'Back to Form' : 'View Enquiries'}
              </button>
              {!showEnquiryList && (
                <>
                  <button
                    onClick={onCreateNewEnquiry || createNewEnquiry}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    New Enquiry
                  </button>
                  <button
                    onClick={goToPreviousStep}
                    disabled={currentStepOrder === 1}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={goToNextStep}
                    disabled={currentStepOrder === steps.length}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </>
              )}
            </div>

            {/* Mobile Quick Actions */}
            <div className="flex lg:hidden items-center space-x-1">
              {!showEnquiryList && (
                <>
                  <button
                    onClick={goToPreviousStep}
                    disabled={currentStepOrder === 1}
                    className="p-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Previous"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={goToNextStep}
                    disabled={currentStepOrder === steps.length}
                    className="p-2 text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Next"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-3 space-y-3">
              {/* User Info on Mobile */}
              <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{user?.email}</span>
                </div>
                <button
                  onClick={signOut}
                  className="flex items-center space-x-1 text-sm text-red-600 hover:text-red-700"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Sign Out</span>
                </button>
              </div>
              
              {/* Mobile Steps Navigation */}
              {!showEnquiryList && (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-900 mb-2">Navigate to Step:</div>
                  <div className="grid grid-cols-2 gap-2">
                    {steps.map((step) => (
                      <button
                        key={step.id}
                        onClick={() => {
                          onStepChange(step.id);
                          setMobileMenuOpen(false);
                        }}
                        className={`p-3 rounded-lg text-left transition-colors ${
                          currentStep === step.id
                            ? 'bg-blue-100 text-blue-700 border-2 border-blue-200'
                            : 'bg-gray-50 text-gray-700 border-2 border-transparent hover:bg-gray-100'
                        }`}
                      >
                        <div className="font-medium">{step.order}. {step.title}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {currentStep === step.id ? 'Current Step' : 'Tap to go'}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Mobile Actions */}
              <div className="space-y-2 pt-3 border-t border-gray-200">
                <button
                  onClick={() => {
                    onToggleEnquiryList();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full p-3 text-left text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  {showEnquiryList ? 'Back to Form' : 'View Enquiries'}
                </button>
                {!showEnquiryList && (
                  <button
                    onClick={() => {
                      (onCreateNewEnquiry || createNewEnquiry)();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full p-3 text-left text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    New Enquiry
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Progress Bar */}
        {!showEnquiryList && (
          <div className="pb-2 px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between text-sm text-gray-500 mb-1">
              <span>Step {currentStepOrder} of {steps.length}</span>
              <span className="hidden sm:inline">{Math.round((currentStepOrder / steps.length) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStepOrder / steps.length) * 100}%` }}
              />
            </div>
            {/* Mobile Step Name */}
            <div className="lg:hidden mt-2 text-center">
              <span className="text-sm font-medium text-gray-900">
                {steps.find(s => s.id === currentStep)?.title}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};